<template>
  <component
    :is="expanded ? 'div' : 'aside'"
    v-if="record"
    :style="
      expanded
        ? { position: 'absolute', inset: 0, zIndex: 5, background: 'var(--surface-ground)', display: 'flex', flexDirection: 'column', animation: 'lesFade .12s ease-out' }
        : { width: `${sideWidth}px`, flexShrink: 0, alignSelf: 'stretch', display: 'flex', flexDirection: 'column', background: 'var(--p-content-background)', borderLeft: '1px solid var(--p-content-border-color)' }
    "
  >
    <!-- header -->
    <header
      :style="{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        padding: '12px 14px',
        borderBottom: '1px solid var(--p-content-border-color)',
        userSelect: 'none',
        cursor: !expanded ? 'default' : 'auto',
      }"
      @dblclick="onHeaderDblClick"
    >
      <button
        v-if="expanded"
        title="Back"
        :style="{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          border: 0,
          background: 'transparent',
          cursor: 'pointer',
          color: 'var(--p-text-muted-color)',
          flexShrink: 0,
        }"
        @click="onBack"
      >
        <i class="pi pi-arrow-left" :style="{ fontSize: '15px' }" />
      </button>
      <div :style="{ flex: 1, minWidth: 0 }">
        <div :style="{ fontSize: '10px', fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--p-surface-400)' }">
          {{ eyebrow }}
        </div>
        <div
          :style="{ fontSize: '15.5px', fontWeight: 800, letterSpacing: '-.01em', color: 'var(--p-text-color)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }"
        >{{ titleText }}</div>
      </div>
      <div :style="{ display: 'flex', alignItems: 'center', gap: '6px' }">
        <template v-if="editing">
          <button type="button" title="Cancel" aria-label="Cancel" :style="btnStyle(false)" @click="cancel"><span>Cancel</span></button>
          <button type="button" title="Save" aria-label="Save" :style="btnStyle(true)" @click="submit">
            <i class="pi pi-check" :style="{ fontSize: '12px' }" /><span>Save</span>
          </button>
        </template>
        <template v-else>
          <button v-if="!confirmDel && !config.hideEdit" type="button" title="Edit" aria-label="Edit" :style="btnStyle(false)" @click="onEdit">
            <i class="pi pi-pencil" :style="{ fontSize: '12px' }" /><span>Edit</span>
          </button>
          <template v-if="hasDelete">
            <span v-if="confirmDel" :style="{ display: 'inline-flex', alignItems: 'center', gap: '6px' }">
              <span :style="{ fontSize: '12px', color: 'var(--p-text-muted-color)' }">Delete?</span>
              <button
                type="button"
                :style="{ height: '30px', padding: '0 11px', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 700, border: '1px solid var(--p-status-danger)', background: 'var(--p-status-danger)', color: 'var(--p-status-danger-contrast)' }"
                @click="emit('delete', record[idKey])"
              >Delete</button>
              <button
                type="button"
                :style="{ height: '30px', padding: '0 9px', borderRadius: '8px', cursor: 'pointer', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 700, border: '1px solid var(--p-content-border-color)', background: 'transparent', color: 'var(--p-text-muted-color)' }"
                @click="confirmDel = false"
              >Cancel</button>
            </span>
            <button v-else type="button" title="Delete" aria-label="Delete" :style="btnStyle(false)" @click="confirmDel = true">
              <i class="pi pi-trash" :style="{ fontSize: '12px' }" />
            </button>
          </template>
        </template>
        <template v-if="!config.hideModeToggle && !expanded">
          <span :style="{ width: '1px', height: '20px', background: 'var(--p-content-border-color)', margin: '0 2px' }" />
          <button type="button" title="Open in new tab" aria-label="Open in new tab" :style="btnStyle(false)" @click="onOpenNewTab">
            <i class="pi pi-external-link" :style="{ fontSize: '12px' }" />
          </button>
        </template>
        <template v-if="!expanded">
          <span :style="{ width: '1px', height: '20px', background: 'var(--p-content-border-color)', margin: '0 2px' }" />
          <button
            title="Close"
            aria-label="Close"
            :style="{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '32px', height: '32px', borderRadius: '8px', border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--p-text-muted-color)', flexShrink: 0 }"
            @click="emit('close')"
          >
            <i class="pi pi-times" :style="{ fontSize: '15px' }" />
          </button>
        </template>
      </div>
    </header>

    <!-- body -->
    <div :style="bodyOuterStyle">
      <div :style="bodyInnerStyle">
        <slot
          v-if="$slots.instance"
          name="instance"
          :record="record"
          :draft="draft"
          :editing="editing"
          :set-field="setField"
          :set-draft="setDraft"
          :register-submit="registerSubmit"
          :is-new="isNew"
          :field-definitions="fieldDefinitions"
          :field-meta="fieldMeta"
          :mode="mode"
          :expanded="expanded"
        />
        <RenderNode v-else-if="config.renderInstance" :node="customBody" />
        <template v-else>
          <section v-for="g in groups" :key="g.label">
            <div
              :style="{ fontSize: '10.5px', fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--p-surface-400)', marginBottom: '9px', paddingBottom: '7px', borderBottom: '1px solid var(--p-content-border-color)' }"
            >{{ g.label }}</div>
            <div :style="groupGridStyle">
              <div v-for="k in g.fields" :key="k" :style="fieldRowStyle">
                <label :style="{ fontSize: '12px', color: 'var(--p-text-muted-color)', display: 'inline-flex', alignItems: 'center', gap: '7px' }">
                  <i v-if="metaFor(k).icon" :class="`pi pi-${metaFor(k).icon}`" :style="{ fontSize: '11px', color: 'var(--p-surface-400)' }" />
                  {{ metaFor(k).label || k }}
                </label>
                <FieldControl
                  v-if="editing"
                  :field-key="k"
                  :field-definitions="fieldDefinitions"
                  :field-meta="fieldMeta"
                  :value="draft[k]"
                  @change="setField(k, $event)"
                />
                <div v-else :style="{ fontSize: '13.5px', color: 'var(--p-text-color)' }">
                  <FormatValue :record="record" :field-key="k" :field-definitions="fieldDefinitions" :field-meta="fieldMeta" />
                </div>
              </div>
            </div>
          </section>
        </template>
      </div>
    </div>
  </component>
</template>

<script setup>
// ── InstancePanel — read-only viewer with an Edit→form toggle ────────────
// The right-hand record panel. Entity-agnostic: renders from fieldDefinitions +
// fieldMeta + a light config (title + field groups), or a custom body via the
// #instance scoped slot / config.renderInstance. Ported 1:1 from
// instance-panel.jsx. See PROMPT §4.4 for the expandOnEdit transition rule.
import { ref, computed, watch } from 'vue'
import FieldControl from '@/admin-collection-layout/lib/FieldControl.vue'
import FormatValue from '@/admin-collection-layout/lib/formatValue.js'
import RenderNode from '@/admin-collection-layout/lib/RenderNode.js'

const props = defineProps({
  record: { type: Object, default: null },
  config: { type: Object, required: true },
  fieldDefinitions: { type: Object, required: true },
  fieldMeta: { type: Object, default: () => ({}) },
  mode: { type: String, default: 'side' },
  isNew: { type: Boolean, default: false },
  sideWidth: { type: Number, default: 420 },
  startEditing: { type: Boolean, default: false },
  // Persists the record and answers whether the write stuck. A function rather
  // than a `save` emit because the answer has to travel back: an emit returns
  // nothing, so the panel used to leave edit mode before the server had spoken
  // and the record watch then wiped the draft. A refusal keeps the boxes.
  commit: { type: Function, default: null },
})
const emit = defineEmits(['mode-change', 'close', 'delete', 'open-new-tab', 'request-edit'])

const idKey = computed(() => props.config.idKey || 'id')
const editing = ref(props.isNew || props.startEditing)
const draft = ref(props.record)
const confirmDel = ref(false)

// Reset edit state whenever a different record is opened (new records open
// straight into the editor).
watch(
  () => props.record && props.record[idKey.value],
  () => {
    editing.value = props.isNew || props.startEditing
    draft.value = props.record
    confirmDel.value = false
    submitHandler.value = null
  },
)
// Keep draft's non-edited fields fresh if the underlying record changes.
watch(
  [() => props.record, editing],
  () => {
    if (!editing.value) draft.value = props.record
  },
)
// Configs that edit in a fullscreen surface jump to expanded mode on Edit and
// drop back to the side overview when editing ENDS. The watch gives the previous
// value for free, so merely opening expanded (e.g. double-click in view mode) is
// never bounced back to side — collapse fires only on a true→false transition.
watch(editing, (now, prev) => {
  if (!props.config.expandOnEdit) return
  if (now && props.mode !== 'expanded') emit('mode-change', 'expanded')
  else if (!now && prev && props.mode === 'expanded') emit('mode-change', 'side')
})

const expanded = computed(() => props.mode === 'expanded')
const groups = computed(
  () => props.config.editGroups || [{ label: 'Details', fields: Object.keys(props.fieldDefinitions.fields || {}) }],
)

function setField(k, v) {
  draft.value = { ...draft.value, [k]: v }
}
function setDraft(updater) {
  draft.value = typeof updater === 'function' ? updater(draft.value) : updater
}
// Optional save bridge: a custom #instance body can register a handler that
// runs its own validation and returns the payload to persist (or `true` to use
// the current draft). Returning a falsy value aborts the save and keeps the
// panel in edit mode so the body can surface validation errors. Absent handler
// → commit the draft as before.
const submitHandler = ref(null)
function registerSubmit(fn) {
  submitHandler.value = typeof fn === 'function' ? fn : null
}
async function submit() {
  let payload = draft.value
  if (submitHandler.value) {
    const result = await submitHandler.value()
    if (!result) return
    payload = result === true ? draft.value : result
  }
  if (props.commit) {
    const ok = await props.commit(payload)
    if (ok === false) return
  }
  editing.value = false
}
// Cancel on a brand-new record discards it entirely; on an existing record it
// just reverts the draft.
function cancel() {
  if (props.isNew) {
    emit('delete', props.record[idKey.value], { silent: true })
    emit('close')
  } else {
    draft.value = props.record
    editing.value = false
  }
}
function onEdit() {
  if (props.config.expandOnEdit && props.mode !== 'expanded') emit('request-edit')
  else editing.value = true
}
function onBack() {
  if (expanded.value) emit('mode-change', 'side')
  else emit('close')
}
function onOpenNewTab() {
  emit('open-new-tab', props.record)
  // (the shell may also handle open-new-tab; fallback expand if no handler acts)
}
function onHeaderDblClick(e) {
  if (!expanded.value && !e.target.closest('button')) emit('mode-change', 'expanded')
}

const renderInstanceArgs = computed(() => ({
  record: props.record,
  draft: draft.value,
  editing: editing.value,
  setField,
  setDraft,
  registerSubmit,
  isNew: props.isNew,
  fieldDefinitions: props.fieldDefinitions,
  fieldMeta: props.fieldMeta,
  mode: props.mode,
  expanded: expanded.value,
}))
const customBody = computed(() => (props.config.renderInstance ? props.config.renderInstance(renderInstanceArgs.value) : null))

const metaFor = (k) => (props.fieldMeta || {})[k] || {}
// Delete is offered whenever the shell wires a delete handler — which it always
// does unless config.hideDelete (mirrors the React `onDelete && …` gate).
const hasDelete = computed(() => !props.config.hideDelete)
function btnStyle(primary) {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    height: '30px',
    padding: primary ? '0 13px' : '0 9px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: '12.5px',
    fontWeight: 700,
    border: `1px solid ${primary ? 'var(--p-primary-500)' : 'var(--p-content-border-color)'}`,
    background: primary ? 'var(--p-primary-500)' : 'transparent',
    color: primary ? 'var(--p-primary-950)' : 'var(--p-text-muted-color)',
  }
}
const eyebrow = computed(() => (props.isNew ? `New ${props.config.kindLabel || 'record'}` : props.config.kindLabel || 'Record'))
const titleText = computed(() => (props.config.primary ? props.config.primary(props.record) : props.record[idKey.value]) || 'Untitled')
const bodyOuterStyle = computed(() => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: expanded.value ? '22px 0' : '16px 16px 22px',
}))
const bodyInnerStyle = computed(() => ({
  maxWidth: expanded.value ? (props.config.instanceMaxWidth != null ? props.config.instanceMaxWidth : 860) : 'none',
  margin: '0 auto',
  padding: expanded.value ? '0 24px' : 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '18px',
}))
const groupGridStyle = computed(() => ({
  display: 'grid',
  gridTemplateColumns: expanded.value ? 'repeat(2, minmax(0,1fr))' : '1fr',
  gap: expanded.value ? '14px 28px' : '12px',
}))
const fieldRowStyle = computed(() => ({
  display: 'grid',
  gridTemplateColumns: expanded.value ? '150px 1fr' : '120px 1fr',
  alignItems: 'center',
  gap: '12px',
}))
</script>
